import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
} from '@dnd-kit/sortable';
// import { useQueryClient } from '@tanstack/react-query';
import { restrictToParentElement, restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';
import useKeyPress, { ESCAPE_KEY } from '../../../hooks/useKeyPress';
import { ICmdTableColumn, ICmdTableSettingsData, ICmdTableSortFilter } from '../types';
import { ACTIONS_KEY, CUSTOMERS_KEY } from '../constants';
import { SortableItem } from './SortableItem';
import { customersAtom } from '../../../store/recoil/customers';
import { arrayOrder, getTableName, saveTableSettings } from '../CmdTableService';
import {
  columnsAtom,
  columnsKeyOrderAtom,
  isTableSettingsMenuOpenedAtom,
  sortFilterAtomFamily,
  tableSettingsAtom,
} from '../CmdTableState';
import useAnalytics from '../../../hooks/useAnalytics';
import { isNotDemo } from '../../../Services/DemoService';

interface ICmdTableSettingsMenuProps {
  tableId: string;
  actionColumnEnabled?: boolean;
  sort?: {
    column: string;
    direction: string;
  };
  tableName: string;
  translationKeys: { [key: string]: string };
  locked?: string[];
}
const WORDS_LIMIT = 20;

function CmdTableSettingsMenu(props: ICmdTableSettingsMenuProps): JSX.Element {
  const [columnsTable, setColumnsTable] = useRecoilState<ICmdTableColumn[]>(columnsAtom);
  const [filteredColumns, setFilteredColumns] = React.useState<ICmdTableColumn[]>(
    columnsTable
      ? columnsTable.filter((i: ICmdTableColumn) => i.key !== ACTIONS_KEY && !i.system)
      : []
  );
  const setOrderForColumns = useSetRecoilState(columnsKeyOrderAtom);
  const { formatMessage: f } = useIntl();
  const setIsTableSettingsMenuOpened = useSetRecoilState(isTableSettingsMenuOpenedAtom);
  const [isResetBtnDisable, setIsResetBtnDisable] = React.useState<boolean>(true);
  const [prevColumns, setPrevColumns] = React.useState<ICmdTableColumn[]>([]);
  const tableSettingsState = useRecoilValue<ICmdTableSettingsData | null>(tableSettingsAtom);
  const sortFilter = useRecoilValue<ICmdTableSortFilter>(sortFilterAtomFamily(props.tableId));
  const setTableSettingsState = useSetRecoilState<ICmdTableSettingsData | null>(tableSettingsAtom);
  const customer = useRecoilValue(customersAtom);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const { trackEvent } = useAnalytics();

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (
      props.locked?.includes(active.id.toString()) ||
      (over && props.locked?.includes(over.id.toString()))
    ) {
      return;
    }
    const oldIndex = filteredColumns.findIndex((item) => item.key === active.id);
    const newIndex = filteredColumns.findIndex((item) => item.key === over?.id);

    let newColumns = [...filteredColumns];

    if (oldIndex === newIndex) {
      const column = { ...newColumns[oldIndex] };
      if (!column.default) {
        column.hidden = !column.hidden;
        newColumns[oldIndex] = column;
      }
    } else {
      newColumns = arrayMove(newColumns, oldIndex, newIndex);
    }

    setFilteredColumns(newColumns);
    trackEvent([`table settings ${getTableName(props.tableId)}`, 'drag', 'Reorder columns']);
  };

  useKeyPress(ESCAPE_KEY, () => {
    setIsTableSettingsMenuOpened(false);
  });

  const handleTableSettings = async (data: ICmdTableColumn[]) => {
    setIsTableSettingsMenuOpened(false);
    const tempOrder = data.map((item) => item.key);
    const tempData: { name: string; hidden: boolean }[] = [];
    const tempColumns: { key: string; hidden: boolean; default: boolean }[] = [];

    data.map((item: { key: string; hidden: boolean; default: boolean }) => {
      tempData.push({ name: item.key, hidden: item.hidden });
      tempColumns.push({ key: item.key, hidden: item.hidden, default: item.default });
    });

    const orderColumns = arrayOrder([...tempColumns], [...tempOrder], 'key');

    setColumnsTable(orderColumns);

    const sortFilterColumn = Object.keys(sortFilter)[0];
    const sortFilterOrder = sortFilter[sortFilterColumn];

    const dataForRequest = {
      ...tableSettingsState,
      columns: [...tempData],
      sort: {
        column: sortFilterColumn,
        direction: sortFilterOrder,
      },
    };
    if (props.sort) {
      dataForRequest.sort = props.sort;
    }
    if (isNotDemo()) {
      const saveSettings = await saveTableSettings(dataForRequest, props.tableName);
      if (saveSettings) {
        setTableSettingsState(saveSettings.value);
      }
    }
  };

  const handleApply = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent([`table settings ${getTableName(props.tableId)}`, 'click', 'Apply settings']);

    const order: { key: string; hidden: boolean }[] = [];

    filteredColumns.map((item) => {
      order.push({ key: item.key, hidden: item.hidden });
    });
    if (props.actionColumnEnabled) {
      // order.push({ key: ACTIONS_KEY, hidden: false });
    }

    setOrderForColumns(order);
    handleTableSettings(filteredColumns);
  };

  const handleDefaultColumns = (columns: ICmdTableColumn[]) => {
    const sortedArray = [
      ...columns.filter((item) => item.key !== ACTIONS_KEY),
      // ...columns.filter((item) => item.key === ACTIONS_KEY),
    ];

    setFilteredColumns(sortedArray ? sortedArray.filter((i) => i.key !== ACTIONS_KEY) : []);
  };

  const handleReset = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent([`table settings ${getTableName(props.tableId)}`, 'click', 'Reset settings']);

    const tempTableSettingOrder: ICmdTableColumn[] = [];

    columnsTable.map((item) => {
      if (customer.length === 1 && item.key === CUSTOMERS_KEY) {
        tempTableSettingOrder.push({ key: item.key, default: item.default, hidden: true });
      } else {
        tempTableSettingOrder.push({ key: item.key, default: item.default, hidden: item.hidden });
      }
    });

    setOrderForColumns(columnsTable);
    handleDefaultColumns(columnsTable);
  };

  React.useEffect(() => {
    if (JSON.stringify(filteredColumns) !== JSON.stringify(prevColumns)) {
      if (prevColumns.length > 0) {
        setIsResetBtnDisable(false);
      }
      setPrevColumns(filteredColumns);
    }
  }, [filteredColumns, prevColumns]);

  const handleLongWords = (word: string) => {
    if (word.length > WORDS_LIMIT) {
      return `${word.slice(0, WORDS_LIMIT)}...`;
    }
    return word;
  };
  const handleTooltipWhenLong = (word: string) => {
    if (word.length > WORDS_LIMIT) {
      return word;
    }
    return '';
  };

  const getItemTitle = (key: string) => {
    if (props.translationKeys?.[key]) {
      return f({ id: `${props.translationKeys[key]}` });
    }
    return 'No translation key';
  };

  return (
    <div className="col-auto mr-3">
      <div className="b-dropdown open" style={{ width: '220px', marginLeft: '-14px' }}>
        <div
          className="b-dropdown__content"
          style={{ position: 'absolute', zIndex: 999, overflow: 'hidden' }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToParentElement, restrictToFirstScrollableAncestor]}
          >
            <SortableContext
              items={filteredColumns.map((item) => item.key)}
              strategy={verticalListSortingStrategy}
            >
              <ul
                className="b-dropdown__list"
                style={{
                  overflowY: 'auto',
                  maxHeight: 'calc(100vh - 260px)',
                  whiteSpace: 'nowrap',
                  overflowX: 'hidden',
                  maxWidth: '220px',
                }}
              >
                {filteredColumns.map((item) => (
                  <SortableItem
                    key={item.key}
                    item={item}
                    isLocked={props.locked?.includes(item.key)}
                  >
                    <i
                      className="cmd-icons cmd-icon-reorder drag-handle"
                      style={
                        props.locked?.includes(item.key)
                          ? {
                              color: '#ccc',
                            }
                          : {}
                      }
                    />
                    <div
                      className="e-checkbox"
                      title={handleTooltipWhenLong(getItemTitle(item.key))}
                    >
                      <label>
                        <input
                          type="checkbox"
                          checked={!item.hidden}
                          disabled={!!item.default || props.locked?.includes(item.key)}
                          data-cy={`tlclite-settings-checkbox-${item.key}`}
                        />
                        <span style={{ fontSize: '13px' }}>
                          {handleLongWords(getItemTitle(item.key))}
                        </span>
                      </label>
                    </div>
                  </SortableItem>
                ))}
              </ul>
            </SortableContext>
          </DndContext>
          <div className="b-dropdown__footer b-dropdown__footer--border-on">
            <button
              type="button"
              onClick={handleReset}
              className={`${
                isResetBtnDisable ? 'e-button e-button--gray e-button--disabled' : 'e-button'
              }`}
              data-cy="tlclite-settings-reset"
            >
              {f({ id: 'form.cancel.tooltip' })}
            </button>
            <button
              onClick={handleApply}
              type="button"
              className="e-button e-button--gray"
              data-cy="tlclite-settings-apply"
            >
              {f({ id: 'table.apply' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CmdTableSettingsMenu;
